import React from "react";
import { ABOUT_DATA } from "../data";
import { Shield, Award, CheckCircle2, Users, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import FadeIn from "../components/FadeIn";
import { useLanguage } from "../context/LanguageContext";
import SEO from "../components/SEO";
import Parallax from "../components/Parallax";

function getOptimizedImage(url, width = 1200) {
  if (!url || !url.includes("assets.auxodev.dk")) return url;
  if (url.includes("/cdn-cgi/image/")) return url;
  return url.replace(
    "https://assets.auxodev.dk/",
    `https://assets.auxodev.dk/cdn-cgi/image/width=${width},quality=85,format=auto/`
  );
}

export default function About() {
  const { lang, t } = useLanguage();

  const valueIcons = [
    <Award className="w-8 h-8 text-stone-600" />,
    <CheckCircle2 className="w-8 h-8 text-stone-600" />,
    <Users className="w-8 h-8 text-stone-600" />,
    <Shield className="w-8 h-8 text-stone-600" />,
  ];

  const homePath = lang === "da" ? "/" : "/en";
  const contactPath = lang === "da" ? "/kontakt" : "/en/contact";
  const servicesPath = lang === "da" ? "/ydelser" : "/en/services";
  const projectsPath = lang === "da" ? "/projekter" : "/en/projects";

  // Custom SEO titles and descriptions for About page (strictly <= 512px title and <= 970px description)
  const seoTitle = lang === "da" 
    ? "Om CPH Collective | Byggeri & Tømrer" 
    : "About CPH Collective | Turnkey & Carpentry";
  const seoDesc = lang === "da"
    ? "Læs om CPH Collective, vores værdier om faglig kvalitet, ansvarlighed og gennemskuelighed i byggeprojekter i København."
    : "Learn about CPH Collective, our values of quality, reliability, and transparency in construction projects in Copenhagen.";

  return (
    <div className="bg-stone-50 text-stone-900 min-h-screen pt-24 md:pt-32">
      {/* SEO Tags */}
      <SEO title={seoTitle} description={seoDesc} />

      {/* Header Section */}
      <section className="py-16 md:py-24 border-b border-stone-200">
        <div className="w-full px-6 md:px-12 lg:px-16">
          <div className="max-w-4xl">
            <Parallax speed={0.04} type="text">
              <FadeIn direction="up">
                <span className="text-xs uppercase tracking-[0.4em] text-stone-500 font-mono block mb-4">
                  {t("about.header.tag")}
                </span>
                <h1 className="text-4xl md:text-6xl lg:text-7xl font-light tracking-tight text-stone-900 uppercase mb-8">
                  {t("about.header.title")}
                </h1>
                <p className="text-stone-600 text-lg md:text-xl font-light leading-relaxed max-w-3xl">
                  {t("about.header.subtitle")}
                </p>
              </FadeIn>
            </Parallax>
          </div>
        </div>
      </section>

      {/* Who We Are & Large Photography */}
      <section className="py-24 md:py-36 border-b border-stone-200">
        <div className="w-full px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-24 items-center">
            {/* Text */}
            <div className="lg:col-span-6">
              <FadeIn direction="right">
                <span className="text-xs uppercase tracking-[0.3em] text-stone-500 font-mono block mb-4">
                  {t("about.who.number")}
                </span>
                <h2 className="text-3xl md:text-5xl font-light tracking-tight text-stone-900 uppercase mb-8">
                  {ABOUT_DATA.whoWeAre.title[lang]}
                </h2>
                <p className="text-stone-700 text-lg font-light leading-relaxed mb-6">
                  {lang === "da" ? (
                    <>
                      Hos{" "}
                      <Link
                        to={homePath}
                        className="text-stone-900 font-medium underline underline-offset-4 decoration-stone-300 hover:decoration-stone-900 transition-colors"
                      >
                        CPH Collective
                      </Link>{" "}
                      er vi en dansk bygge- og tømrervirksomhed med fokus på kvalitet, ansvarlighed og professionel projektstyring. Vores tilgang er baseret på klar kommunikation, praktiske løsninger og respekt for tidsplaner og budgetter.
                    </>
                  ) : (
                    <>
                      At{" "}
                      <Link
                        to={homePath}
                        className="text-stone-900 font-medium underline underline-offset-4 decoration-stone-300 hover:decoration-stone-900 transition-colors"
                      >
                        CPH Collective
                      </Link>
                      , we are a Danish construction and carpentry company with a focus on quality, responsibility, and professional project management. Our approach is based on clear communication, practical solutions, and respect for timelines and budgets.
                    </>
                  )}
                </p>
                <p className="text-stone-600 text-base font-light leading-relaxed">
                  {t("about.who.subtext")}
                </p>
              </FadeIn>
            </div>

            {/* Image */}
            <div className="lg:col-span-6">
              <FadeIn direction="left" delay={150}>
                <div className="relative h-[400px] md:h-[500px] overflow-hidden border border-stone-200">
                  <Parallax speed={-0.08} type="background" className="w-full h-full">
                    <img
                      src={getOptimizedImage("https://assets.auxodev.dk/assets/503cf246-11be-4a10-9327-e937ebb53ecb/cph-collective-byggepladsservice-sikkerhed-styring.jpg", 1200)}
                      alt={lang === "da" ? "CPH Collective - Byggeledelse, kvalitetssikring og koordinering på byggepladsen" : "CPH Collective - Construction management, quality assurance, and site coordination"}
                      className="w-full h-full object-cover object-center"
                    />
                  </Parallax>
                  <div className="absolute inset-0 bg-stone-950/10 z-10 pointer-events-none" />
                </div>
              </FadeIn>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values - Grid Layout */}
      <section className="py-24 md:py-36 border-b border-stone-200 bg-stone-100/50">
        <div className="w-full px-6 md:px-12 lg:px-16">
          <FadeIn direction="up">
            <div className="max-w-3xl mb-16">
              <span className="text-xs uppercase tracking-[0.3em] text-stone-500 font-mono block mb-4">
                {t("about.values.number")}
              </span>
              <h2 className="text-3xl md:text-5xl font-light tracking-tight text-stone-900 uppercase">
                {t("about.values.title")}
              </h2>
            </div>
          </FadeIn>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {ABOUT_DATA.values.map((value, idx) => (
              <FadeIn key={idx} direction="up" delay={idx * 100} className="h-full">
                <div
                  className="border border-stone-200 bg-white p-8 hover:border-stone-300 hover:shadow-sm transition-all duration-300 h-full"
                >
                  <div className="mb-6">{valueIcons[idx]}</div>
                  <h3 className="text-lg font-light uppercase tracking-wider text-stone-900 mb-3">
                    {value.title[lang]}
                  </h3>
                  <p className="text-stone-600 text-sm font-light leading-relaxed">
                    {value.description[lang]}
                  </p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us - Stats & Details */}
      <section className="py-24 md:py-36 border-b border-stone-200">
        <div className="w-full px-6 md:px-12 lg:px-16">
          <FadeIn direction="up">
            <div className="max-w-3xl mb-16">
              <span className="text-xs uppercase tracking-[0.3em] text-stone-500 font-mono block mb-4">
                {t("about.why.number")}
              </span>
              <h2 className="text-3xl md:text-5xl font-light tracking-tight text-stone-900 uppercase">
                {t("about.why.title")}
              </h2>
              <p className="text-stone-600 text-lg font-light mt-4 leading-relaxed">
                {t("about.why.subtitle")}
              </p>
            </div>
          </FadeIn>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {ABOUT_DATA.whyChooseUs.map((item, idx) => (
              <FadeIn key={idx} direction="up" delay={idx * 100}>
                <div
                  className="border border-stone-200 bg-white p-8 md:p-12 hover:border-stone-300 hover:shadow-sm transition-all duration-300 flex gap-6"
                >
                  <div className="text-stone-500 font-mono text-lg font-light">
                    0{idx + 1}
                  </div>
                  <div>
                    <h3 className="text-xl font-light uppercase tracking-wider text-stone-900 mb-4">
                      {item.title[lang]}
                    </h3>
                    <p className="text-stone-600 text-sm md:text-base font-light leading-relaxed">
                      {item.description[lang]}
                    </p>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Cross-linking Section to other subpages */}
      <section className="py-20 bg-stone-100/70 border-t border-stone-200">
        <div className="w-full px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="p-8 bg-white border border-stone-200 flex flex-col justify-between">
              <div>
                <span className="text-xs font-mono uppercase tracking-[0.25em] text-stone-400 block mb-3">
                  {lang === "da" ? "Vores Ydelser" : "Our Services"}
                </span>
                <h3 className="text-xl font-light uppercase tracking-tight text-stone-900 mb-3">
                  {lang === "da" ? "Totalentreprise & Tømrerarbejde" : "Turnkey & Carpentry"}
                </h3>
                <p className="text-stone-600 text-sm font-light leading-relaxed mb-6">
                  {lang === "da"
                    ? "Se alle vores faglige ydelser inden for totalentreprise, projektering, renovering og byggepladsservice."
                    : "Explore our specialized services including turnkey construction, engineering, renovation, and site services."}
                </p>
              </div>
              <Link
                to={servicesPath}
                className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-stone-900 hover:text-stone-600 transition-colors border-b border-stone-900 pb-1 self-start"
              >
                <span>{lang === "da" ? "Se vores ydelser" : "View our services"}</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>

            <div className="p-8 bg-white border border-stone-200 flex flex-col justify-between">
              <div>
                <span className="text-xs font-mono uppercase tracking-[0.25em] text-stone-400 block mb-3">
                  {lang === "da" ? "Dokumenteret Erfaring" : "Proven Experience"}
                </span>
                <h3 className="text-xl font-light uppercase tracking-tight text-stone-900 mb-3">
                  {lang === "da" ? "Se Gennemførte Projekter" : "View Completed Projects"}
                </h3>
                <p className="text-stone-600 text-sm font-light leading-relaxed mb-6">
                  {lang === "da"
                    ? "Gennemse vores cases og se konkrete eksempler på vores håndværksmæssige kvalitet i København."
                    : "Browse our case studies and see tangible examples of our quality craftsmanship in Copenhagen."}
                </p>
              </div>
              <Link
                to={projectsPath}
                className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-stone-900 hover:text-stone-600 transition-colors border-b border-stone-900 pb-1 self-start"
              >
                <span>{lang === "da" ? "Udforsk referencer" : "Explore references"}</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-stone-100 py-24 border-t border-stone-200">
        <div className="w-full px-6 md:px-12 lg:px-16 text-center">
          <div className="max-w-3xl mx-auto">
            <FadeIn direction="up">
              <h2 className="text-3xl md:text-4xl font-light uppercase tracking-tight text-stone-900 mb-6">
                {lang === "da" ? "Klar til at starte dit projekt?" : "Ready to Start Your Project?"}
              </h2>
              <p className="text-stone-600 text-base md:text-lg font-light mb-10 leading-relaxed">
                {lang === "da"
                  ? "Kontakt os i dag for en uforpligtende samtale om dit næste erhvervsbyggeri eller tømrerprojekt."
                  : "Contact us today for a non-binding consultation about your next commercial construction or carpentry project."}
              </p>
              <Link
                to={contactPath}
                className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-white bg-stone-900 hover:bg-stone-800 transition-all duration-500 px-8 py-4 font-semibold font-mono"
              >
                {t("cta.requestQuote")}
                <ArrowRight className="w-4 h-4" />
              </Link>
            </FadeIn>
          </div>
        </div>
      </section>
    </div>
  );
}
